class DrawerMenu extends HTMLElement {
  constructor() {
    super();
    this.drawer = null;
    this.menuBtn = null;
    this.overlay = null;
    this.sidebarWidth = 320; // Default width
    this.announcementBar = null;
    this._handleResize = null;
    
    // Bind methods
    this.toggleDrawerMenu = this.toggleDrawerMenu.bind(this);
    this.closeDrawerMenu = this.closeDrawerMenu.bind(this);
    this.closeDrawer = this.closeDrawer.bind(this);
    this.openSubmenu = this.openSubmenu.bind(this);
    this.closeSubmenu = this.closeSubmenu.bind(this);
    this.closeAllSubmenus = this.closeAllSubmenus.bind(this);
    this.handleEscape = this.handleEscape.bind(this);
  }

  connectedCallback() {
    this.drawer = this;
    this.menuBtn = document.getElementById("mobile-menu-btn");
    this.announcementBar = document.querySelector(".essential_annoucement_bar_wrapper");
    
    // Get overlay element (create if doesn't exist)
    this.overlay = document.querySelector("[data-header-overlay]");
    if (!this.overlay) {
      this.createOverlay();
    }

    if (!this.menuBtn) {
      console.warn('DrawerMenu: Menu button not found');
      return;
    }

    // Store initial width
    this.sidebarWidth = this.drawer.offsetWidth || 320;

    this.initializeDrawer();
    this.positionBelowAnnouncementBar();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.closeDrawerMenu);
    document.removeEventListener('keydown', this.handleEscape);
    if (this.menuBtn) {
      this.menuBtn.removeEventListener('click', this.toggleDrawerMenu);
    }
    if (this.overlay) {
      this.overlay.removeEventListener('click', this.closeDrawerMenu);
    }
    if (this._handleResize) {
      window.removeEventListener('resize', this._handleResize);
    }
  }

  isMobile() {
    return window.innerWidth < 992; // Match DDM's breakpoint
  }

  getAnnouncementOffset() {
    // Announcement bar is injected by an app; it is NOT sticky.
    // offsetHeight remains > 0 even when scrolled away, so use viewport geometry.
    this.announcementBar = document.querySelector(".essential_annoucement_bar_wrapper");
    if (!this.announcementBar) return 0;

    const rect = this.announcementBar.getBoundingClientRect();
    if (rect.bottom <= 0) return 0; // fully scrolled off the top
    // Use a small threshold (2px) to account for rounding/subpixel rendering after scrolling
    if (rect.top > 2) return 0;     // not covering the top edge
    return Math.max(0, Math.round(Math.min(rect.bottom, rect.height)));
  }

  positionBelowAnnouncementBar() {
    // Set --drawer-height based on current viewport (captures visible height at open time)
    const drawerHeight = window.innerHeight;
    document.documentElement.style.setProperty('--drawer-height', `${drawerHeight}px`);

    const announcementHeight = this.getAnnouncementOffset();
    const isMobile = this.isMobile();
    
    // Use var(--drawer-height) for mobile, vh for desktop
    if (isMobile) {
      // Mobile: Use var(--drawer-height) to ensure full viewport coverage
      if (announcementHeight > 0) {
        this.drawer.style.top = `${announcementHeight}px`;
        // Use calc with var(--drawer-height) - CSS fallback handles no-JS case
        this.drawer.style.height = `calc(var(--drawer-height, 100svh) - ${announcementHeight}px)`;
        if (this.overlay) {
          // Overlay uses inset:0 for full coverage - only adjust top if announcement bar exists
          this.overlay.style.top = `${announcementHeight}px`;
          this.overlay.style.bottom = '0';
          this.overlay.style.removeProperty('height'); // Let inset handle height
        }
      } else {
        // No announcement bar - let CSS handle it (uses var(--drawer-height))
        this.drawer.style.top = '0';
        this.drawer.style.height = ''; // Remove inline style to use CSS
        if (this.overlay) {
          // Overlay uses inset:0 - remove any inline positioning
          this.overlay.style.removeProperty('top');
          this.overlay.style.removeProperty('bottom');
          this.overlay.style.removeProperty('height');
        }
      }
    } else {
      // Desktop: Use vh units (works fine on desktop, no browser UI issues)
      if (announcementHeight > 0) {
        this.drawer.style.top = `${announcementHeight}px`;
        this.drawer.style.height = `calc(100vh - ${announcementHeight}px)`;
        if (this.overlay) {
          // Overlay uses inset:0 for full coverage - only adjust top if announcement bar exists
          this.overlay.style.top = `${announcementHeight}px`;
          this.overlay.style.bottom = '0';
          this.overlay.style.removeProperty('height'); // Let inset handle height
        }
      } else {
        this.drawer.style.top = '0';
        this.drawer.style.height = ''; // Remove inline style to use CSS (dvh on desktop)
        if (this.overlay) {
          // Overlay uses inset:0 - remove any inline positioning
          this.overlay.style.removeProperty('top');
          this.overlay.style.removeProperty('bottom');
          this.overlay.style.removeProperty('height');
        }
      }
    }
  }

  createOverlay() {
    this.overlay = document.createElement('div');
    this.overlay.setAttribute('data-header-overlay', '');
    this.overlay.className = 'drawer-mobile-menu__overlay'; // Match CSS class name
    
    const overlayTop = `${this.getAnnouncementOffset()}px`;
    
    // Set only the dynamic styles inline, let CSS handle the rest
    // Explicitly set z-index lower than drawer to ensure proper stacking
    this.overlay.style.cssText = `
      position: fixed;
      top: ${overlayTop};
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 999;
    `;
    // Append overlay BEFORE drawer in DOM to ensure drawer is on top
    const drawer = document.getElementById('drawer-mobile-menu');
    if (drawer && drawer.parentNode) {
      drawer.parentNode.insertBefore(this.overlay, drawer);
    } else {
      document.body.appendChild(this.overlay);
    }
    this.overlay.addEventListener('click', this.closeDrawerMenu);
  }

  toggleDrawerMenu(event) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
    }
    
    const isOpening = !this.drawer.classList.contains('is-visible');
    
    if (isOpening) {
      this.openDrawer();
    } else {
      this.closeDrawer();
    }
  }

  openDrawer() {
    // Close all submenus first to reset state
    this.closeAllSubmenus();
    
    // Reset all submenu transforms to ensure they start at translateX(0)
    const allSubmenus = this.drawer.querySelectorAll('[data-header-submenu]');
    allSubmenus.forEach(submenu => {
      submenu.style.removeProperty('transform');
      submenu.style.removeProperty('transition');
    });
    
    // Reset width to base width (320px on desktop, 85% on mobile)
    if (!this.isMobile()) {
      this.drawer.style.width = '320px';
      this.drawer.classList.remove('is-submenu-visible');
    } else {
      this.drawer.style.width = '85%';
    }
    
    // Position below announcement bar first
    this.positionBelowAnnouncementBar();
    
    // Make visible but still off-screen
    this.drawer.style.visibility = 'visible';
    
    // Remove inline transform style so CSS can control it
    this.drawer.style.removeProperty('transform');
    
    // Use requestAnimationFrame to ensure browser applies changes before adding class
    requestAnimationFrame(() => {
      // Show overlay - let CSS handle visibility and opacity
      if (this.overlay) {
        this.overlay.classList.add('is-visible');
      }
      
      // Open drawer - add class which CSS will use to transform
      this.drawer.setAttribute('aria-expanded', 'true');
      this.drawer.classList.add('is-visible');
      
      // Simply disable scroll - no position:fixed needed
      document.body.classList.add('overflow-hidden', 'mobile-menu-open');
      
      // Add escape key listener
      document.addEventListener('keydown', this.handleEscape);
    });
  }

  closeDrawer() {
    // Don't close submenus yet - keep them visible during close animation
    // This ensures the drawer slides out with visible content
    
    // Use requestAnimationFrame to ensure proper sequencing
      requestAnimationFrame(() => {
        // Force a reflow
        this.drawer.offsetHeight;
        
        requestAnimationFrame(() => {
          // Set transition duration to match cart drawer (0.3s)
          // This ensures both drawers animate at the same speed
          this.drawer.style.transition = 'transform 0.3s ease, width 0.3s ease';
          
          // Simply remove is-visible from drawer - exactly like manual removal in inspector
          // This should trigger the drawer slide-out animation
          // The submenu is inside the drawer, so it will slide out with it
          this.drawer.classList.remove('is-visible');
          
          // Wait for drawer transition to complete, then clean up
          this.drawer.addEventListener('transitionend', () => {
            if (!this.drawer.classList.contains('is-visible')) {
              // Clean up after animation completes
              this.drawer.setAttribute('aria-expanded', 'false');
              this.drawer.style.visibility = 'hidden';
              this.drawer.style.transform = 'translateX(-100%)';
              
              // Reset transition duration back to CSS default (remove inline style)
              this.drawer.style.removeProperty('transition');
              
              // Reset width and submenu state
              if (!this.isMobile()) {
                this.drawer.style.width = '320px';
              } else {
                this.drawer.style.width = '85%';
              }
              this.drawer.classList.remove('is-submenu-visible');
              
              // Clean up submenus
              this.closeAllSubmenus();
              
              // Hide overlay
              if (this.overlay) {
                this.overlay.classList.remove('is-visible');
              }
              
              // Simply re-enable scroll - no scroll restoration needed
              document.body.classList.remove('overflow-hidden', 'mobile-menu-open');
              
              // Remove escape key listener
              document.removeEventListener('keydown', this.handleEscape);
            }
          }, { once: true });
        });
      });
  }

  handleEscape(event) {
    if (event.key === 'Escape' || event.keyCode === 27) {
      if (this.drawer.classList.contains('is-visible')) {
        this.closeDrawer();
      }
    }
  }

  closeDrawerMenu(event) {
    // Don't close if clicking inside drawer
    if (this.drawer.contains(event.target)) {
      return;
    }
    
    // Don't close if clicking menu button
    if (event.target === this.menuBtn || (this.menuBtn && this.menuBtn.contains(event.target))) {
      return;
    }
    
    // Close drawer
    if (this.drawer.classList.contains('is-visible')) {
      this.closeDrawer();
    }
  }

  openSubmenu(button) {
    const submenuId = button.getAttribute('data-open-submenu');
    if (!submenuId) return;
    
    // Find matching submenu
    const submenus = this.drawer.querySelectorAll('[data-header-submenu]');
    const submenu = Array.from(submenus).find(s => s.dataset.headerSubmenu === submenuId);
    
    if (!submenu) return;
    
    const isDesktop = !this.isMobile();
    
    // Close any other open submenus first
    this.closeAllSubmenus();
    
    // Reset this submenu's transform to ensure it starts at translateX(0)
    submenu.style.removeProperty('transform');
    submenu.style.removeProperty('transition');
    
    // Add classes to submenu
    submenu.classList.add('is-visible', 'is-transformed');
    
    // Desktop: Expand drawer width if submenu has images
    if (isDesktop) {
      this.drawer.classList.add('is-submenu-visible');
      
      // Check if submenu has image push section
      const hasImages = submenu.querySelector('.drawer-menu__submenu__push');
      if (hasImages) {
        // 320px (menu) + 320px (images) = 640px
        this.drawer.style.width = '640px';
      } else {
        // Just the menu column, keep at 320px
        this.drawer.style.width = '320px';
      }
    }
    
    // Update aria-expanded
    button.setAttribute('aria-expanded', 'true');
  }

  closeSubmenu(button) {
    this.closeAllSubmenus();
    
    // Reset width on desktop to base width
    if (!this.isMobile()) {
      this.drawer.classList.remove('is-submenu-visible');
      this.drawer.style.width = '320px';
    }
  }
  

  closeAllSubmenus() {
    const submenus = this.drawer.querySelectorAll('[data-header-submenu]');
    submenus.forEach(submenu => {
      submenu.classList.remove('is-visible', 'is-transformed');
    });
    
    // Reset aria-expanded on all buttons
    const openButtons = this.drawer.querySelectorAll('[data-open-submenu]');
    openButtons.forEach(btn => btn.setAttribute('aria-expanded', 'false'));
  }

  initializeDrawer() {
    // Menu toggle button
    if (this.menuBtn) {
      this.menuBtn.addEventListener('click', this.toggleDrawerMenu, { passive: false });
    }
    
    // Close buttons in drawer - always close the full drawer
    // Simple handler: if button has data-close-header-sidebar, close drawer
    const drawerCloseButtons = this.drawer.querySelectorAll('[data-close-header-sidebar]');
    drawerCloseButtons.forEach(btn => {
      const handleClose = (e) => {
        // Stop event immediately to prevent any other handlers
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Always close the drawer - no conditions
        this.toggleDrawerMenu();
      };
      
      btn.addEventListener('click', handleClose, { capture: true });
      btn.addEventListener('touchend', handleClose, { capture: true, passive: false });
    });
    
    // Submenu open buttons
    const openButtons = this.drawer.querySelectorAll('[data-open-submenu]');
    openButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.openSubmenu(button);
      });
    });
    
    // Submenu back buttons (close submenu, go back to main menu)
    // Simple handler: if button has data-close-submenu (and NOT data-close-header-sidebar), go back
    const submenuBackButtons = this.drawer.querySelectorAll('[data-close-submenu]');
    submenuBackButtons.forEach(button => {
      const handleBack = (e) => {
        // Don't handle if this is actually a close button (has data-close-header-sidebar)
        if (button.hasAttribute('data-close-header-sidebar')) {
          return;
        }
        
        e.preventDefault();
        e.stopPropagation();
        this.closeSubmenu(button);
      };
      
      button.addEventListener('click', handleBack);
      button.addEventListener('touchend', handleBack, { passive: false });
    });
    
    // Close on outside click
    document.addEventListener('click', this.closeDrawerMenu, { passive: true });
    
    // Initialize world times
    this.initializeWorldTimes();
    
    // Handle resize
    this._handleResize = () => {
      this.positionBelowAnnouncementBar();
      // Reset width if drawer is open but no submenu is visible
      if (this.drawer.classList.contains('is-visible') && !this.drawer.classList.contains('is-submenu-visible')) {
        if (!this.isMobile()) {
          this.drawer.style.width = '320px';
        } else {
          this.drawer.style.width = '85%';
        }
      }
    };
    window.addEventListener('resize', this._handleResize, { passive: true });
  }

  initializeWorldTimes() {
    const timeElements = this.drawer.querySelectorAll('[data-timezone]');

    timeElements.forEach(el => {
      const timezone = el.getAttribute('data-timezone');
      const cityName = el.getAttribute('data-city');

      try {
        const time = new Date().toLocaleTimeString('en-GB', {
          timeZone: timezone,
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });

        el.textContent = `${cityName} ${time}`;
      } catch (error) {
        console.warn('Error formatting time for', timezone, error);
        el.textContent = `${cityName} --:--`;
      }
    });
  }
}

// Register custom element
if (!customElements.get('drawer-menu')) {
  customElements.define('drawer-menu', DrawerMenu);
}
